import React from "react";
import { AdditionalFieldHtml } from "../AdditionalFieldHtml";
import {
  getRegistrationCommerceItems,
  hasEmbeddedGuest,
  isParticipantCheckInEligible,
} from "../../utils/checkIn";

const getStateForRegistration = (checkInData, registration) =>
  checkInData?.checkInStates?.[
    registration?.documentId || registration?.id
  ] || {};

const actionKey = (action) =>
  [
    action.registrationId,
    action.target,
    action.fieldName || "",
    action.unitIndex ?? "",
  ].join(":");

const requestCheckInAction = (onAction, action) => {
  if (
    action.checked === false &&
    !window.confirm("Are you sure you want to undo this check-in?")
  ) {
    return;
  }

  onAction(action);
};

const CheckedInButton = ({ action, busyActionKey, label, onAction }) => (
  <button
    aria-label={`${label} Checked-In; click to undo`}
    className="btn btn-success"
    disabled={busyActionKey !== null}
    onClick={() => requestCheckInAction(onAction, action)}
    type="button"
  >
    {busyActionKey === actionKey(action) ? "Saving…" : "Checked-In"}
  </button>
);

const PurchaseSummary = ({ checkInData, registration }) => {
  if (!registration) return null;

  const purchasedItems = getRegistrationCommerceItems(
    checkInData.eventData,
    registration,
  );
  if (purchasedItems.length === 0) return null;

  const hasTotals = ["subCost", "taxCost", "totalCost"].some(
    (propertyName) =>
      registration[propertyName] !== null &&
      registration[propertyName] !== undefined &&
      registration[propertyName] !== "",
  );

  return (
    <section className="mb-4 rounded border border-secondary-subtle p-3">
      <h3 className="mb-3">Purchase Summary</h3>
      {checkInData.guestRegistration ? (
        <h4 className="mb-3">
          {registration.firstName} {registration.lastName}
        </h4>
      ) : null}
      <div className="row pb-2 fw-bold">
        <div className="col-6">Item</div>
        <div className="col-2 text-end">Qty</div>
        <div className="col-4 text-end">Cost</div>
      </div>
      {purchasedItems.map((item) => (
        <div className="row py-1" key={item.fieldName}>
          <div className="col-6">
            <AdditionalFieldHtml html={item.fieldLabel || item.fieldName} />
          </div>
          <div className="col-2 text-end">{item.quantity}</div>
          <div className="col-4 text-end">
            {registration[`${item.fieldName}Cost`] ?? "—"}
          </div>
        </div>
      ))}
      {hasTotals ? (
        <div className="mt-3 border-top pt-2">
          {registration.subCost !== null &&
          registration.subCost !== undefined &&
          registration.subCost !== "" ? (
            <div className="d-flex justify-content-end gap-3">
              <strong>Subtotal:</strong>
              <span>{registration.subCost}</span>
            </div>
          ) : null}
          {registration.taxCost !== null &&
          registration.taxCost !== undefined &&
          registration.taxCost !== "" ? (
            <div className="d-flex justify-content-end gap-3">
              <strong>
                Tax
                {registration.fieldTax ?? checkInData.eventData?.paymentTax
                  ? ` (${registration.fieldTax ?? checkInData.eventData.paymentTax}%)`
                  : ""}
                :
              </strong>
              <span>{registration.taxCost}</span>
            </div>
          ) : null}
          {registration.totalCost !== null &&
          registration.totalCost !== undefined &&
          registration.totalCost !== "" ? (
            <div className="d-flex justify-content-end gap-3">
              <strong>Total:</strong>
              <span>{registration.totalCost}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

const ParticipantCard = ({
  busyActionKey,
  checkInData,
  kind,
  onAction,
  registration,
}) => {
  if (!registration) return null;

  const checkInState = getStateForRegistration(checkInData, registration);
  const commerceItems = getRegistrationCommerceItems(
    checkInData.eventData,
    registration,
    checkInState,
  ).filter((item) => item.fieldCheckIn);
  const eligible = isParticipantCheckInEligible(registration.status);
  const participantCheckedIn = registration.status === "Checked-In";
  const participantAction = {
    checked: !participantCheckedIn,
    registrationId: registration.id,
    target: "participant",
  };
  const participantLabel = kind === "guest" ? "Guest" : "Attendee";

  return (
    <section className="mb-4 rounded border border-secondary-subtle p-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
        <div>
          <h3 className="mb-1">
            {participantLabel}: {registration.firstName} {registration.lastName}
          </h3>
          <div>
            {participantCheckedIn ? (
              <span className="fw-bold text-success">Checked-In</span>
            ) : (
              registration.status || "Status unavailable"
            )}
            {registration.adyenPaymentStatus
              ? ` - ${registration.adyenPaymentStatus}`
              : ""}
          </div>
        </div>
        {eligible ? (
          participantCheckedIn ? (
            <CheckedInButton
              action={participantAction}
              busyActionKey={busyActionKey}
              label={participantLabel}
              onAction={onAction}
            />
          ) : (
            <button
              className="btn btn-theme"
              disabled={busyActionKey !== null}
              onClick={() => onAction(participantAction)}
              type="button"
            >
              {busyActionKey === actionKey(participantAction)
                ? "Saving…"
                : `Check In ${participantLabel}`}
            </button>
          )
        ) : (
          <p className="mb-0 text-muted">
            {registration.status || "This registration"} cannot be checked in.
          </p>
        )}
      </div>

      {commerceItems.length > 0 ? (
        <div className="mt-4">
          <h4>Commerce Items</h4>
          <div className="d-grid gap-3">
            {commerceItems.map((item) => {
              const checkedCount = item.units.filter(
                (unit) => unit.checkedIn,
              ).length;

              return (
                <article
                  className="rounded border border-secondary-subtle p-3"
                  key={item.fieldName}
                >
                  <div className="d-flex flex-wrap justify-content-between gap-2">
                    <div>
                      <strong>
                        <AdditionalFieldHtml html={item.fieldLabel || item.fieldName} />
                      </strong>
                      <div className="small text-muted">
                        Quantity: {item.quantity}
                        {item.fieldCheckIn
                          ? ` · Checked in: ${checkedCount}`
                          : ""}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 d-grid gap-2">
                    {item.units.map((unit) => {
                      const itemAction = {
                        checked: !unit.checkedIn,
                        fieldName: item.fieldName,
                        registrationId: registration.id,
                        target: "item",
                        unitIndex: unit.unitIndex,
                      };

                      return (
                        <div
                          className="d-flex align-items-center justify-content-between gap-3 rounded bg-light p-2"
                          key={unit.unitIndex}
                        >
                          <span>Unit {unit.unitIndex + 1}</span>
                          {unit.checkedIn ? (
                            <CheckedInButton
                              action={itemAction}
                              busyActionKey={busyActionKey}
                              label={`${item.fieldName} unit ${unit.unitIndex + 1}`}
                              onAction={onAction}
                            />
                          ) : (
                            <button
                              aria-label={`Check in ${item.fieldName} unit ${unit.unitIndex + 1}`}
                              className="btn btn-sm btn-theme"
                              disabled={busyActionKey !== null}
                              onClick={() => onAction(itemAction)}
                              type="button"
                            >
                              {busyActionKey === actionKey(itemAction)
                                ? "Saving…"
                                : "Check In"}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
};

const EmbeddedGuestCard = ({
  busyActionKey,
  checkInData,
  onAction,
  registration,
}) => {
  if (!hasEmbeddedGuest(registration)) return null;

  const state = getStateForRegistration(checkInData, registration);
  const checkedIn = Boolean(state.embeddedGuest);
  const guestAction = {
    checked: !checkedIn,
    registrationId: registration.id,
    target: "embeddedGuest",
  };

  return (
    <section className="mb-4 rounded border border-secondary-subtle p-3">
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3">
        <div>
          <h3 className="mb-1">
            Guest: {registration.guestFirstName} {registration.guestLastName}
          </h3>
          <div>
            {checkedIn ? (
              <span className="fw-bold text-success">Checked-In</span>
            ) : (
              "Registered"
            )}
          </div>
        </div>
        {checkedIn ? (
          <CheckedInButton
            action={guestAction}
            busyActionKey={busyActionKey}
            label="Guest"
            onAction={onAction}
          />
        ) : (
          <button
            className="btn btn-theme"
            disabled={busyActionKey !== null}
            onClick={() => onAction(guestAction)}
            type="button"
          >
            {busyActionKey === actionKey(guestAction)
              ? "Saving…"
              : "Check In Guest"}
          </button>
        )}
      </div>
    </section>
  );
};

export const RegistrationCheckInPanel = ({
  busyActionKey = null,
  checkInData,
  onAction,
}) => {
  if (!checkInData?.primaryRegistration) return null;

  return (
    <div data-testid="registration-check-in-panel">
      <ParticipantCard
        busyActionKey={busyActionKey}
        checkInData={checkInData}
        kind="attendee"
        onAction={onAction}
        registration={checkInData.primaryRegistration}
      />
      <ParticipantCard
        busyActionKey={busyActionKey}
        checkInData={checkInData}
        kind="guest"
        onAction={onAction}
        registration={checkInData.guestRegistration}
      />
      {!checkInData.guestRegistration ? (
        <EmbeddedGuestCard
          busyActionKey={busyActionKey}
          checkInData={checkInData}
          onAction={onAction}
          registration={checkInData.primaryRegistration}
        />
      ) : null}
      {!checkInData.checkInOnly && <PurchaseSummary
        checkInData={checkInData}
        registration={checkInData.primaryRegistration}
      />}
      {!checkInData.checkInOnly && <PurchaseSummary
        checkInData={checkInData}
        registration={checkInData.guestRegistration}
      />}
    </div>
  );
};

export { actionKey };
